import type { Account } from '../types'

import { ProductName, type ProductType, PRODUCTS } from '../constants/products'
import { parseBibeHostname } from './bibeUtils'

import { getActiveProduct } from '../../exports/Navigator/utils/getActiveProductFromUrl'

export function getProductUrl(product: ProductType): string {
  const { bibePrefix, effectiveHostname, envModifier } = parseBibeHostname(
    window.location.hostname,
  )
  const currentProductDetails: ProductType | null = getActiveProduct()
  const currentProductBaseSubdomain =
    currentProductDetails?.subdomain || effectiveHostname.split('.')[0]

  let targetOriginalServiceHostname = effectiveHostname
  if (
    currentProductBaseSubdomain &&
    product.subdomain !== currentProductBaseSubdomain
  ) {
    targetOriginalServiceHostname = effectiveHostname.replace(
      currentProductBaseSubdomain,
      product.subdomain,
    )
  } else if (!currentProductBaseSubdomain) {
    targetOriginalServiceHostname = effectiveHostname.replace(
      effectiveHostname.split('.')[0],
      product.subdomain,
    )
  }
  let finalTargetHostname = `${bibePrefix}${targetOriginalServiceHostname}`
  if (product.id === ProductName.ACCOUNT) {
    finalTargetHostname = `${targetOriginalServiceHostname}`
  }
  const port = envModifier === 'local.' ? product.localPort : ''
  const path = product.pathname ?? ''
  return path !== ''
    ? `https://${finalTargetHostname}${port}/${path}`
    : `https://${finalTargetHostname}${port}`
}

export function getUrlEnvModifier(): string {
  const { envModifier } = parseBibeHostname()
  return envModifier
}

export type ComposerUrlArgs = {
  socialChannelId: string
  cta: string
}

export function getPublishComposerURL({
  socialChannelId,
  cta,
}: ComposerUrlArgs): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/profile/${socialChannelId}/tab/queue?auto_open_composer=true&profile_id=${socialChannelId}&cta=${cta}`
}

export function getPublishComposerDraftURL({
  socialChannelId,
  cta,
}: ComposerUrlArgs): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/profile/${socialChannelId}/tab/queue?auto_open_composer_as_draft=true&profile_id=${socialChannelId}&cta=${cta}`
}

export function getPostingScheduleURL(args: { profileId: string }): string {
  const { profileId } = args
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/channels/${profileId}/settings?tab=posting-schedule`
}

export function getMigrationHubUrl(cta?: string): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.ACCOUNT])
  const ctaParam = cta ? `/?cta=${cta}` : ``
  return `${baseUrl}/new-plans${ctaParam}`
}

export function getBillingPageUrl(cta: string | null): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  const ctaParam = cta ? `/?cta=${cta}` : ``
  return `${baseUrl}/settings/billing${ctaParam}`
}

export function getBillingPaymentMethodUrl(): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/settings/billing/#payment-method`
}

export function getAccountBaseUrl(): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.ACCOUNT])
  return baseUrl
}

export function getAccountUrl(subpath = ''): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.ACCOUNT])
  return `${baseUrl}/${subpath}`
}

export function getNewSettingsUrl(subpath = ''): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.NEWSETTINGS])
  return `${baseUrl}/${subpath}`
}

export function redirectToUrl(redirectURI: string): void {
  window.location.assign(redirectURI)
}

export function getStartPageUrl(subpath = ''): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.START_PAGE])
  return `${baseUrl}/${subpath}`
}

export function isInPublish(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('publish')
}

export function isOnPublishChannelsRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('/channels')
}

export function isOnPublishProfileRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('/profile')
}

export function isOnPublishAllChannelsRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('all-channels')
}

export function isOnPublishCalendarRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('calendar')
}

export function isInAccount(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('account')
}

export function isOnChannelSettingsRoute(): boolean {
  const isInPublishProduct = isInPublish()

  if (!isInPublishProduct) {
    return false
  }

  const pathname = window.location.pathname
  return Boolean(pathname.match(/\/settings\/channels/))
}

export function isOnAccountChannelsRoute(): boolean {
  const isAccountProduct = isInAccount()

  if (!isAccountProduct) {
    return false
  }

  const pathname = window.location.pathname
  return pathname.includes('channels')
}

export function getSelectedChannelIdFromProfileRoute(): string {
  const pathname = window.location.pathname
  return pathname.split('/')[2]
}

export function getSelectedChannelIdFromChannelsRoute(): string {
  const pathWithoutQueryString = window.location.pathname.split('?')[0]
  return pathWithoutQueryString.split('/')[2]
}

export function getSelectedChannelIdFromAllChannelsRoute(): string[] {
  const urlParams = new URLSearchParams(window.location.search)

  return urlParams.getAll('channels[]')
}

export function getSelectedChannelsFromPublishingRoute(): string[] {
  const isProfileRoute = isOnPublishProfileRoute()
  const isChannelsRoute = isOnPublishChannelsRoute()
  const isAllChannelsRoute = isOnPublishAllChannelsRoute()
  const isCalendarRoute = isOnPublishCalendarRoute()
  const shouldAttempttoGetProfileIdFromURL =
    isProfileRoute || isChannelsRoute || isAllChannelsRoute || isCalendarRoute

  if (!shouldAttempttoGetProfileIdFromURL) return []

  if (isProfileRoute) {
    const selectedChannelId = getSelectedChannelIdFromProfileRoute()
    return selectedChannelId ? [selectedChannelId] : []
  }

  if (isChannelsRoute) {
    const selectedChannelId = getSelectedChannelIdFromChannelsRoute()
    return selectedChannelId ? [selectedChannelId] : []
  }

  if (isAllChannelsRoute || isCalendarRoute) {
    const selectedChannelIds = getSelectedChannelIdFromAllChannelsRoute()
    return selectedChannelIds
  }

  return []
}

export function isInAnalyze(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('analyze')
}

export function isOnAnalyzeOverviewRoute(): boolean {
  const isInAnalyzeProduct = isInAnalyze()

  if (!isInAnalyzeProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('overview')
}

export function getSelectedChannelIdFromOverviewRoute(): string {
  const pathname = window.location.pathname
  return pathname.split('/')[3]
}

export function getSelectedChannelsFromAnalyzeRoute(): string[] {
  const isOverviewRoute = isOnAnalyzeOverviewRoute()

  if (!isOverviewRoute) return []

  const selectedChannelId = getSelectedChannelIdFromOverviewRoute()

  return selectedChannelId ? [selectedChannelId] : []
}

export function isInStartPage(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('start-page')
}

export function getSelectedChannelIdFromStartPageRoute(): string | null {
  const pathname = window.location.pathname
  const channelId = pathname.split('/')[1]

  // Onboarding is the only other value which can be found in the first position of the path
  if (channelId === 'onboarding') return null

  return channelId
}

export function getSelectedChannelsFromStartPageRoute(): string[] {
  const isStartPageRoute = isInStartPage()

  if (!isStartPageRoute) return []

  const selectedChannelId = getSelectedChannelIdFromStartPageRoute()

  return selectedChannelId ? [selectedChannelId] : []
}

export function isInEngage(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('engage')
}

export function getSelectedChannelIdFromEngageRoute(): string {
  const pathname = window.location.pathname
  return pathname.split('/')[1]
}

export function getSelectedChannelsFromEngageRoute(): string[] {
  const isInEngageProduct = isInEngage()

  if (!isInEngageProduct) return []

  const selectedChannelId = getSelectedChannelIdFromEngageRoute()

  return selectedChannelId ? [selectedChannelId] : []
}

export function getPortFromURL(baseUrl: string): string {
  const port = baseUrl.match(/(?<port>:\d+)/)?.groups?.port
  return port || ''
}

export function getQueryParameters(baseUrl: string): string {
  const query = baseUrl.match(/\?(?<query>.*)$/)?.groups?.query
  // NOTE: returned with a starting "&", because it is always used after the
  // initial query parameter, "?redirect="
  return query ? `?${encodeURI(query)}` : ''
}

export function getProductPath(baseUrl: string): string {
  const result = baseUrl.match(/https*:\/\/(.+)\.buffer\.com/)
  let productPath = baseUrl
  if (result instanceof Array) {
    ;[, productPath] = result
  }
  return productPath
}

function getRedirectUrl(baseUrl: string): string {
  const productPath = getProductPath(baseUrl)
  const port = getPortFromURL(baseUrl)

  return `https://${productPath}.buffer.com${port}`
}

function getRedirectUrlWithParams(baseUrl: string): string {
  return `${getRedirectUrl(baseUrl)}${
    window.location.pathname
  }${getQueryParameters(baseUrl)}${encodeURI(window.location.hash)}`
}

function getLoginOrLogoutUrl(baseUrl = '', loginOrLogoutPath: string): string {
  const { bibePrefix } = parseBibeHostname(window.location.hostname)
  const envModifier = getUrlEnvModifier()
  const result = `https://${bibePrefix}login.${envModifier}buffer.com/${loginOrLogoutPath}?redirect=${getRedirectUrlWithParams(
    baseUrl,
  )}`
  return result
}

export function getLogoutUrl(baseUrl: string): string {
  return getLoginOrLogoutUrl(baseUrl, 'logout')
}

export function getLoginUrl(baseUrl: string): string {
  return getLoginOrLogoutUrl(baseUrl, 'login')
}

export function getTeamManageUrl(
  user: Pick<Account, 'currentOrganization'>,
  subpath = '',
): string {
  const envModifier = getUrlEnvModifier()
  const orgId = user.currentOrganization?.id
  return orgId
    ? `https://${
        envModifier || ''
      }buffer.com/manage/${orgId}/team-members/${subpath}`
    : `https://${envModifier || ''}buffer.com`
}

export function getManageUrl(): string {
  const envModifier = getUrlEnvModifier()
  return `https://${envModifier || ''}buffer.com/manage/team-members/`
}

export function getTeamInviteUrl(user: Account): string {
  return getTeamManageUrl(user, 'invite')
}

export function getPublishUrl(subpath = ''): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/${subpath}`
}

export function getBillingUrl(): string {
  return getAccountUrl('billing')
}

export function getManageChannelsURL(): string {
  return getPublishUrl('settings/channels')
}

export function getChannelsQueueUrl({
  channelId,
}: {
  channelId: string
}): string {
  return getPublishUrl(`channels/${channelId}?tab=queue`)
}

export function getChannelSettingsUrl({
  channelId,
}: {
  channelId: string
}): string {
  return getPublishUrl(`channels/${channelId}/settings`)
}

export function getStopImpersonationUrl(): string | null {
  const { hostname } = window.location

  if (!hostname.endsWith('buffer.com') && hostname !== 'localhost') {
    return null
  }

  return `https://admin${
    hostname.includes('local') ? '-next.local' : ''
  }.buffer.com/clearImpersonation`
}

export function getGlobalActionCTAFromQueryParams(): string | null {
  const urlParams = new URLSearchParams(window.location.search)

  return urlParams.get('globalActionCTA')
}
